import React, { useState } from 'react';
import { Button, FormControl } from 'react-bootstrap';
import { Deal, RiskAssessment, RiskLevel } from '../models/shared/Deal';
import { useText } from '../context/TextContext';
import { useUser } from '../context/UserContext';
import { useOrganization } from '../context/OrganizationContext';
import DealModalSection from './DealModalSection';
import Gravatar from './Gravatar';
import { AdvancedButton } from './AdvancedButton';
import { formatDate } from '../utils/date';

interface Props {
    deal: Deal;
    onDealUpdate: (update: Partial<Deal>) => Promise<void>;
    className?: string;
    readOnly?: boolean;
}

const RISK_VARIANTS: Record<RiskLevel, string> = {
    low: 'success',
    medium: 'warning',
    high: 'danger',
};

export default function RiskAssessmentSection(props: Props) {
    const text = useText();
    const { user } = useUser();
    const { members } = useOrganization();
    const [selectedLevel, setSelectedLevel] = useState<RiskLevel | null>(null);
    const [notes, setNotes] = useState('');
    const [processing, setProcessing] = useState(false);

    const assessments = props.deal.riskAssessments || [];

    const handleAdd = async () => {
        if (!selectedLevel || !user?.email) return;
        setProcessing(true);
        const entry: RiskAssessment = {
            performedAt: new Date(),
            performedBy: user.email,
            riskLevel: selectedLevel,
            notes: notes.trim() || undefined,
        };
        await props.onDealUpdate({ riskAssessments: [...assessments, entry] });
        setSelectedLevel(null);
        setNotes('');
        setProcessing(false);
    };

    const latest = assessments[assessments.length - 1];

    return (
        <DealModalSection
            title={text.riskAssessment}
            sizeDependencies={[props.deal.riskAssessments]}
            alwaysVisibleChildren={
                latest ? (
                    <span className={`badge bg-${RISK_VARIANTS[latest.riskLevel]} me-1`}>
                        {text[`risk${latest.riskLevel.charAt(0).toUpperCase() + latest.riskLevel.slice(1)}` as 'riskLow' | 'riskMedium' | 'riskHigh']}
                    </span>
                ) : (
                    <span className="text-muted small">{text.noRiskAssessments}</span>
                )
            }
        >
            <div className="d-flex flex-column gap-3">
                <div className="d-flex flex-column gap-2">
                    {assessments.map((a, i) => {
                        const member = members.find(m => m.email === a.performedBy);
                        return (
                            <div key={i} className="border rounded p-2">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    {member && <Gravatar identifier={member.email} size="xs" default="wavatar" />}
                                    <small className="text-muted">
                                        <strong>{a.performedBy}</strong>
                                        {' • '}
                                        {formatDate(a.performedAt)}
                                    </small>
                                    <span className={`badge bg-${RISK_VARIANTS[a.riskLevel]}`}>
                                        {text[`risk${a.riskLevel.charAt(0).toUpperCase() + a.riskLevel.slice(1)}` as 'riskLow' | 'riskMedium' | 'riskHigh']}
                                    </span>
                                </div>
                                {a.notes && <div style={{ whiteSpace: 'pre-wrap' }} className="small">{a.notes}</div>}
                            </div>
                        );
                    })}
                </div>

                {!props.readOnly && (
                    <div className="d-flex flex-column gap-2">
                        <div className="d-flex gap-2">
                            {(['low', 'medium', 'high'] as RiskLevel[]).map(level => (
                                <Button
                                    key={level}
                                    size="sm"
                                    variant={selectedLevel === level ? RISK_VARIANTS[level] : `outline-${RISK_VARIANTS[level]}`}
                                    onClick={() => setSelectedLevel(level)}
                                >
                                    {text[`risk${level.charAt(0).toUpperCase() + level.slice(1)}` as 'riskLow' | 'riskMedium' | 'riskHigh']}
                                </Button>
                            ))}
                        </div>
                        <FormControl
                            as="textarea"
                            rows={2}
                            placeholder={text.riskNotes}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            disabled={processing}
                        />
                        <div className="d-flex justify-content-end">
                            <AdvancedButton
                                processing={processing}
                                variant="outline-primary"
                                size="sm"
                                disabled={!selectedLevel}
                                onClick={handleAdd}
                            >
                                {text.addRiskAssessment}
                            </AdvancedButton>
                        </div>
                    </div>
                )}
            </div>
        </DealModalSection>
    );
}
